import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ManagementSection } from './ManagementSection';
import { Button } from '../../components/ui/Button';
import { Toggle } from '../../components/ui/Toggle';
import { Typography } from '../../components/ui/Typography';
import { trpc } from '../../lib/trpc';

interface RegeneratedNoticeProps {
  onClose: () => void;
}

/** Acknowledgement shown once a regenerate succeeds; Escape or OK closes it. */
const RegeneratedNotice = ({ onClose }: RegeneratedNoticeProps) => {
  const okRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    okRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="qr-regenerated-message"
        className="w-full max-w-md rounded-lg bg-gray-50 p-6 shadow-xl space-y-6"
      >
        <Typography variant="bodyMedium" className="text-gray-900" as="p">
          <span id="qr-regenerated-message">
            QR code has been regenerated. You will now need to print new codes.
          </span>
        </Typography>
        <div className="flex justify-end">
          <button
            ref={okRef}
            type="button"
            onClick={onClose}
            className="min-h-[44px] min-w-[88px] rounded-md bg-primary-700 px-4 text-white hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export const CheckInQrSection = () => {
  const { id } = useParams<{ id: string }>();
  const venueId = id || '';
  const utils = trpc.useUtils();
  const [confirming, setConfirming] = useState(false);
  const [showRegenerated, setShowRegenerated] = useState(false);
  const closeNotice = useCallback(() => setShowRegenerated(false), []);

  const settings = trpc.checkIn.venueQrSettings.useQuery({ venueId }, { enabled: !!venueId });
  const onSuccess = () => {
    void utils.checkIn.venueQrSettings.invalidate({ venueId });
  };
  const setRequired = trpc.checkIn.setVenueQrRequired.useMutation({ onSuccess });
  const regenerate = trpc.checkIn.regenerateVenueQrToken.useMutation({
    onSuccess: () => {
      setConfirming(false);
      setShowRegenerated(true);
      onSuccess();
    },
  });

  const requiresQr = settings.data?.requiresQr === true;
  const saving = setRequired.isPending || regenerate.isPending;
  const mutationError = setRequired.error ?? regenerate.error;

  return (
    <ManagementSection
      title="Check-in QR code"
      description="Choose whether visitors must scan a code at your venue to check in, and print the code."
      status="active"
    >
      {settings.isLoading && (
        <Typography variant="bodySmall" className="text-gray-600">
          Loading QR settings...
        </Typography>
      )}

      {settings.error && (
        <Typography variant="bodySmall" className="text-error">
          Unable to load QR settings: {settings.error.message}
        </Typography>
      )}

      {settings.data && (
        <div className="space-y-4">
          <Toggle
            checked={requiresQr}
            disabled={saving}
            onChange={(required) => setRequired.mutate({ venueId, required })}
            label="Require a QR scan to check in"
            description="Visitors must be within 100m and scan the code at your tasting bar. Useful where several tasting rooms are close together."
          />

          {requiresQr && (
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/wineries/${venueId}/manage/check-in-qr`}
                className="inline-flex items-center min-h-[44px] px-4 rounded-md bg-primary-700 text-white hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2"
              >
                Print QR code
              </Link>
              {!confirming && (
                <Button
                  variant="outline"
                  className="min-h-[44px]"
                  disabled={saving}
                  onClick={() => setConfirming(true)}
                >
                  Regenerate code
                </Button>
              )}
            </div>
          )}

          {requiresQr && confirming && (
            <div className="rounded-md border border-gray-200 bg-gray-100 p-4 space-y-3">
              <Typography variant="bodySmall" className="text-gray-900">
                Every printed code for this venue stops working immediately. You'll need to reprint
                table tents.
              </Typography>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="danger"
                  className="min-h-[44px]"
                  loading={regenerate.isPending}
                  disabled={saving}
                  onClick={() => regenerate.mutate({ venueId })}
                >
                  Regenerate
                </Button>
                <Button
                  variant="ghost"
                  className="min-h-[44px]"
                  disabled={regenerate.isPending}
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {mutationError && (
            <Typography variant="bodySmall" className="text-error" as="p">
              {mutationError.message}
            </Typography>
          )}
        </div>
      )}

      {showRegenerated && <RegeneratedNotice onClose={closeNotice} />}
    </ManagementSection>
  );
};
